import { User, LoginCredentials, SignupData } from '../../types/auth';

export class AuthService {
  private baseUrl: string;
  private headers: Record<string, string>;

  constructor(config: { baseUrl: string; headers: Record<string, string> }) {
    this.baseUrl = config.baseUrl;
    this.headers = config.headers;
  }

  async login(credentials: LoginCredentials): Promise<{ user: User; token: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/login`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(credentials)
      });

      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        console.error('Failed to parse response:', e);
        throw new Error('Invalid server response');
      }

      if (!response.ok) {
        throw new Error(data?.message || 'Login failed');
      }

      if (!data?.user || !data?.token) {
        console.error('Invalid response data:', data);
        throw new Error('Invalid response format');
      }

      // Ensure id is always set (handle _id from MongoDB)
      if (!data.user.id && data.user._id) {
        data.user.id = data.user._id;
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async signup(data: SignupData): Promise<{ user: User; token: string }> {
    try {
      
      const response = await fetch(`${this.baseUrl}/auth/signup`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(data)
      });

      let responseData;
      try {
        const text = await response.text();
        responseData = text ? JSON.parse(text) : null;
      } catch (e) {
        console.error('Failed to parse response:', e);
        throw new Error('Invalid server response');
      }

      if (!response.ok) {
        // If the server returns validation errors array, format them
        if (responseData?.errors && Array.isArray(responseData.errors)) {
          const errorMessages = responseData.errors.map((err: any) => err.msg || err.message).join(', ');
          throw new Error(errorMessages || responseData?.message || 'Signup failed');
        }
        throw new Error(responseData?.message || 'Signup failed');
      }

      if (!responseData?.user || !responseData?.token) {
        console.error('Invalid response data:', responseData);
        throw new Error('Invalid response format');
      }

      // Ensure id is always set (handle _id from MongoDB)
      if (!responseData.user.id && responseData.user._id) {
        responseData.user.id = responseData.user._id;
      }

      return responseData;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    const response = await fetch(`${this.baseUrl}/auth/logout`, {
      method: 'POST',
      headers: this.headers
    });

    if (!response.ok) {
      throw new Error('Logout failed');
    }
  }

  async getCurrentUser(): Promise<User> {
    try {

      const response = await fetch(`${this.baseUrl}/auth/me`, {
        method: 'GET',
        headers: this.headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user information');
      }

      const userData = await response.json();
      if (!userData.id && userData._id) {
        userData.id = userData._id;
      }

      return userData;
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  }

  async updateUser(_userId: string, data: Partial<User>): Promise<User> {
    const response = await fetch(`${this.baseUrl}/auth/me`, {
      method: 'PUT',
      headers: this.headers,
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to update user');
    }

    return response.json();
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/auth/password`, {
      method: 'PUT',
      headers: this.headers,
      body: JSON.stringify({ currentPassword, newPassword })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to change password');
    }

    return response.json();
  }
}